import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Button,
  Card,
  CardHeader,
  CardBody,
  Input,
  Chip,
  Spinner
} from '@heroui/react';
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline';

function UpdateProfile() {
  const { currentUser, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  
  const [displayName, setDisplayName] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (currentUser) {
      setDisplayName(currentUser.displayName || '');
      setPhotoURL(currentUser.photoURL || '');
    }
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await updateUserProfile({ displayName, photoURL });
      setSuccess('Profile updated successfully!');
      setTimeout(() => navigate('/profile'), 2000); 
    } catch (err) {
      setError('Failed to update profile. Please try again.');
      console.error("Error updating profile:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900">
        <Spinner size="lg" color="primary" />
        <p className="ml-4 text-xl text-gray-700 dark:text-gray-300">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900 p-6">
      <div className="container mx-auto max-w-lg">
        <Card className="shadow-xl hover:shadow-2xl transition-shadow duration-300">
          <CardHeader className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Update Profile</h1>
            <Button
              variant="light"
              onPress={() => navigate('/profile')}
              startContent={<ArrowLeftIcon className="h-5 w-5" />}
            >
              Back to Profile
            </Button>
          </CardHeader>
          <CardBody>
            {error && (
              <Chip color="danger" variant="flat" className="w-full mb-4">
                {error}
              </Chip>
            )}
            {success && (
              <Chip color="success" variant="flat" className="w-full mb-4">
                {success}
              </Chip>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                type="text"
                label="Display Name"
                placeholder="Enter your display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                variant="bordered"
                fullWidth
              />
              <Input
                type="url"
                label="Photo URL"
                placeholder="Enter URL for your profile picture"
                value={photoURL}
                onChange={(e) => setPhotoURL(e.target.value)}
                variant="bordered"
                fullWidth
              />
              <Button
                type="submit"
                color="primary"
                fullWidth
                isLoading={loading}
                startContent={!loading && <CheckIcon className="h-5 w-5" />}
              >
                {loading ? 'Updating...' : 'Save Changes'}
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default UpdateProfile;
